import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Paper,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle,
  People,
  Star,
  LocationOn,
  TrendingUp,
  ExpandMore,
  ExpandLess,
  Send,
  Assessment,
  AutoAwesome,
  History,
  Public,
  PersonAdd,
  Search,
  Info
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import aiService from '../../services/aiService';
import contractorService from '../../services/contractorService';
import projectService from '../../services/projectService';
import { brand, text, bg, border, shadow, status as statusColors, easing } from '../../theme/colors';

/**
 * Enhanced Post-Creation Flow Component
 * Provides multiple paths for finding and selecting contractors:
 * 1. AI-Matched Contractors (smart recommendations)
 * 2. Previous Contractors (contractors you've worked with)
 * 3. Post to Marketplace (let contractors bid)
 * 4. Invite Specific Contractor (direct invitation)
 */
const PostCreationFlow = ({ projectId, projectData, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0); // 0: AI Match, 1: Previous, 2: Marketplace, 3: Invite
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [matchedContractors, setMatchedContractors] = useState([]);
  const [previousContractors, setPreviousContractors] = useState([]);
  const [expandedContractor, setExpandedContractor] = useState(null);
  const [error, setError] = useState('');
  
  // Marketplace posting state
  const [marketplacePosting, setMarketplacePosting] = useState({
    allowProposals: true,
    deadline: '',
    minQualifications: '',
    additionalNotes: ''
  });
  const [postingToMarketplace, setPostingToMarketplace] = useState(false);
  
  // Contractor invitation state
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    if (projectId && projectData) {
      loadContractorData();
    }
  }, [projectId, projectData]);
  
  const loadContractorData = async () => {
    setLoading(true);
    try {
      // Load AI-matched contractors
      await loadAndMatchContractors();
      
      // Load previous contractors
      await loadPreviousContractors();
    } catch (err) {
      console.error('Failed to load contractor data:', err);
      setError('Failed to load contractor information');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAndMatchContractors = async () => {
    try {
      // Fetch available contractors
      const contractorsResult = await contractorService.getContractors({
        location: typeof projectData.location === 'string' ? projectData.location : projectData.location?.address,
        specialty: projectData.taskCategory || projectData.fullProjectType
      });
      
      if (contractorsResult.success && contractorsResult.data) {
        setContractors(contractorsResult.data);
        
        // Run AI matching
        const matchResult = await aiService.matchContractors(
          {
            projectType: projectData.projectType,
            taskType: projectData.specificTask || projectData.fullProjectType,
            description: projectData.description,
            budget: projectData.budget,
            location: typeof projectData.location === 'string' ? projectData.location : projectData.location?.address
          },
          contractorsResult.data
        );
        
        if (matchResult.success) {
          setMatchedContractors(matchResult.data.matches);
        }
      }
    } catch (err) {
      console.error('Failed to load and match contractors:', err);
    }
  };
  
  const loadPreviousContractors = async () => {
    try {
      // Fetch user's previous projects to get contractors they've worked with
      const projectsResult = await projectService.getUserProjects(user.id);
      
      if (projectsResult.success && projectsResult.data) {
        // Extract unique contractors from previous projects
        const contractorMap = new Map();
        
        projectsResult.data.forEach(project => {
          if (project.contractor && project.contractor_id) {
            if (!contractorMap.has(project.contractor_id)) {
              contractorMap.set(project.contractor_id, {
                ...project.contractor,
                id: project.contractor_id,
                projectsCount: 1,
                lastProject: project.title,
                lastProjectDate: project.updated_at || project.created_at
              });
            } else {
              const existing = contractorMap.get(project.contractor_id);
              existing.projectsCount += 1;
            }
          }
        });
        
        setPreviousContractors(Array.from(contractorMap.values()));
      }
    } catch (err) {
      console.error('Failed to load previous contractors:', err);
    }
  };
  
  const handleInviteContractor = async (contractorId) => {
    try {
      // Update project with invited contractor
      await projectService.inviteContractor(projectId, contractorId);
      
      navigate(`/projects/${projectId}`);
      onClose();
    } catch (err) {
      console.error('Failed to invite contractor:', err);
      setError('Failed to invite contractor');
    }
  };
  
  const handleSelectPreviousContractor = async (contractorId) => {
    try {
      // Assign contractor to project
      await projectService.assignContractor(projectId, contractorId);
      
      navigate(`/projects/${projectId}`);
      onClose();
    } catch (err) {
      console.error('Failed to assign contractor:', err);
      setError('Failed to assign contractor');
    }
  };
  
  const handlePostToMarketplace = async () => {
    setPostingToMarketplace(true);
    try {
      // Update project to allow bids
      await projectService.updateProject(projectId, {
        status: 'open_for_bids',
        proposal_deadline: marketplacePosting.deadline || null,
        min_qualifications: marketplacePosting.minQualifications || null,
        additional_notes: marketplacePosting.additionalNotes || null
      });
      
      // Notify nearby contractors about the new project (non-blocking)
      contractorService.notifyNewProject(projectId).catch(() => null);
      
      navigate(`/projects/${projectId}`);
      onClose();
    } catch (err) {
      console.error('Failed to post to marketplace:', err);
      setError('Failed to post project to marketplace');
    } finally {
      setPostingToMarketplace(false);
    }
  };
  
  const handleInviteByEmail = async () => {
    if (!inviteName || !inviteEmail) {
      setError('Please enter the contractor\'s name and email address');
      return;
    }

    setInviting(true);
    try {
      await projectService.inviteContractorByEmail(projectId, {
        email: inviteEmail,
        name: inviteName,
        phone: invitePhone || undefined,
        message: inviteMessage
      });

      setError('');
      alert('Invitation sent successfully!');
      setInviteName('');
      setInviteEmail('');
      setInvitePhone('');
      setInviteMessage('');
    } catch (err) {
      console.error('Failed to send invitation:', err);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setInviting(false);
    }
  };
  
  // Render AI-Matched Contractors Tab
  const renderAIMatchedTab = () => {
    if (loading && !matchedContractors.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Analyzing contractors and finding the best matches...
          </Typography>
        </Box>
      );
    }
    
    if (matchedContractors.length === 0 && contractors.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <People sx={{ fontSize: 48, color: text.tertiary, mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: text.primary, mb: 1 }}>
            No contractors found yet
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
            Post your project to the marketplace and contractors will come to you with proposals.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Public />}
            onClick={() => setActiveTab(2)}
            sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
          >
            Post to Marketplace
          </Button>
        </Box>
      );
    }

    // Show raw contractor results when AI matching returns no matches but contractors exist in the area
    if (matchedContractors.length === 0 && contractors.length > 0) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" icon={<People />} sx={{ mb: 2, bgcolor: `${brand.primary}08`, border: `1px solid ${brand.primary}20`, '& .MuiAlert-icon': { color: brand.primary } }}>
            <Typography variant="body2">
              We found <strong>{contractors.length} contractor{contractors.length > 1 ? 's' : ''}</strong> in your area.
              {projectData?.location && ` Showing results near ${typeof projectData.location === 'string' ? projectData.location : projectData.location?.address || 'your location'}.`}
            </Typography>
          </Alert>
          <List>
            {contractors.slice(0, 5).map((contractor) => (
              <ListItem
                key={contractor.id}
                sx={{ border: `1px solid ${border.light}`, borderRadius: 2, mb: 1.5 }}
              >
                <ListItemAvatar>
                  <Avatar src={contractor.avatar}>{contractor.name?.[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{contractor.name}</Typography>
                      {contractor.verified && (
                        <Chip label="Verified" size="small" sx={{ bgcolor: `${statusColors.success}14`, color: statusColors.success, fontWeight: 600, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {contractor.rating && (
                        <>
                          <Star sx={{ fontSize: 14, color: brand.accent }} />
                          <Typography variant="caption">{contractor.rating}</Typography>
                        </>
                      )}
                      {contractor.location && (
                        <>
                          <LocationOn sx={{ fontSize: 14, color: text.tertiary }} />
                          <Typography variant="caption">{contractor.location}</Typography>
                        </>
                      )}
                      {contractor.type && (
                        <Chip label={contractor.type} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                  }
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Send />}
                  onClick={() => handleInviteContractor(contractor.id)}
                  sx={{ borderColor: brand.primary, color: brand.primary, '&:hover': { bgcolor: `${brand.primary}08` } }}
                >
                  Invite
                </Button>
              </ListItem>
            ))}
          </List>
          {contractors.length > 5 && (
            <Button
              fullWidth
              onClick={() => { navigate('/contractors'); onClose(); }}
              sx={{ color: brand.primary, mt: 1 }}
            >
              View all {contractors.length} contractors →
            </Button>
          )}
          {/* Independent site check — the first thing a diaspora owner
              wants before strangers quote on a site they can't see. */}
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: `${brand.primary}0d`, border: `1px solid ${brand.primary}33` }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Want a verified look at the site before contractors quote?</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              An independent Eqaya verifier visits, sends dated GPS-tagged photos and a written report. From USD 50, usually within 72 hours.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => { navigate(`/property-check?utm_source=project_created&project=${encodeURIComponent(projectId || '')}`); onClose(); }} sx={{ borderColor: brand.primary, color: brand.primary }}>
              Book a property check
            </Button>
          </Box>
        </Box>
      );
    }
    
    return (
      <List sx={{ mt: 2 }}>
        {matchedContractors.slice(0, 5).map((match, index) => {
          const contractor = contractors.find(c => c.id === match.contractorId);
          if (!contractor) return null;
          
          const isExpanded = expandedContractor === match.contractorId;
          
          return (
            <React.Fragment key={match.contractorId}>
              <ListItem
                sx={{
                  border: 1,
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  mb: 2,
                  flexDirection: 'column',
                  alignItems: 'stretch'
                }}
              >
                <ListItemButton
                  onClick={() => setExpandedContractor(isExpanded ? null : match.contractorId)}
                  sx={{ width: '100%' }}
                >
                  <ListItemAvatar>
                    <Avatar src={contractor.profile_image}>
                      {contractor.name?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {contractor.name}
                        </Typography>
                        <Chip
                          label={`${match.matchScore}% Match`}
                          color={match.matchScore >= 80 ? 'success' : match.matchScore >= 60 ? 'primary' : 'default'}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                          <Typography variant="body2">
                            {contractor.rating || 'N/A'} ({contractor.reviews_count || 0} reviews)
                          </Typography>
                          <LocationOn sx={{ fontSize: 16, ml: 1 }} />
                          <Typography variant="body2">
                            {contractor.location}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <IconButton>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </ListItemButton>
                
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Why this match?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {match.reasoning}
                    </Typography>
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Strengths
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {match.strengths?.map((strength, idx) => (
                        <Chip key={idx} label={strength} size="small" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                    
                    {match.considerations && match.considerations.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Considerations
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {match.considerations.map((consideration, idx) => (
                            <Typography key={idx} variant="body2" color="text.secondary">
                              • {consideration}
                            </Typography>
                          ))}
                        </Box>
                      </>
                    )}
                    
                    <Button
                      variant="contained"
                      startIcon={<Send />}
                      fullWidth
                      onClick={() => handleInviteContractor(match.contractorId)}
                    >
                      Invite to Bid
                    </Button>
                  </Box>
                </Collapse>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    );
  };
  
  // Render Previous Contractors Tab
  const renderPreviousContractorsTab = () => {
    if (loading && !previousContractors.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading your previous contractors...
          </Typography>
        </Box>
      );
    }
    
    if (previousContractors.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          You haven't worked with any contractors yet. Try the AI-matched contractors or post to the marketplace.
        </Alert>
      );
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
          These are contractors you've worked with before. Select one to work with them again.
        </Alert>
        
        <List>
          {previousContractors.map((contractor) => (
            <ListItem
              key={contractor.id}
              sx={{
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 2,
                mb: 2
              }}
            >
              <ListItemAvatar>
                <Avatar src={contractor.profile_image}>
                  {contractor.name?.[0]}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {contractor.name}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {contractor.projectsCount} project{contractor.projectsCount > 1 ? 's' : ''} together
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last project: {contractor.lastProject}
                    </Typography>
                  </Box>
                }
              />
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => handleSelectPreviousContractor(contractor.id)}
              >
                Work Together Again
              </Button>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };
  
  // Render Marketplace Tab
  const renderMarketplaceTab = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" icon={<Public />} sx={{ mb: 3 }}>
          Post your project to the marketplace and let contractors submit proposals. You'll review and select the best one.
        </Alert>
        
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Marketplace Posting Settings
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Proposal Deadline (Optional)"
              value={marketplacePosting.deadline}
              onChange={(e) => setMarketplacePosting(prev => ({ ...prev, deadline: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Set a deadline for contractors to submit proposals"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Minimum Qualifications (Optional)"
              placeholder="e.g., 5+ years experience, licensed, insured..."
              value={marketplacePosting.minQualifications}
              onChange={(e) => setMarketplacePosting(prev => ({ ...prev, minQualifications: e.target.value }))}
              helperText="Specify any requirements for contractors"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes (Optional)"
              placeholder="Any other information contractors should know..."
              value={marketplacePosting.additionalNotes}
              onChange={(e) => setMarketplacePosting(prev => ({ ...prev, additionalNotes: e.target.value }))}
              helperText="Add any extra details or instructions"
            />
          </Box>
          
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Public />}
            onClick={handlePostToMarketplace}
            disabled={postingToMarketplace}
          >
            {postingToMarketplace ? <CircularProgress size={24} /> : 'Post to Marketplace'}
          </Button>
        </Paper>
      </Box>
    );
  };
  
  // Render Invite Contractor Tab
  const renderInviteTab = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" icon={<PersonAdd />} sx={{ mb: 3 }}>
          Know a contractor you'd like to work with? Invite them directly by email.
        </Alert>
        
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Invite Contractor
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Contractor Name *"
              placeholder="Enter contractor's full name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="email"
              label="Email Address *"
              placeholder="contractor@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Phone Number (Optional)"
              placeholder="+263 77 123 4567"
              value={invitePhone}
              onChange={(e) => setInvitePhone(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Personal Message (Optional)"
              placeholder="Add a personal message to your invitation..."
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              helperText="This message will be included in the invitation email"
            />
          </Box>

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Send />}
            onClick={handleInviteByEmail}
            disabled={inviting || !inviteName || !inviteEmail}
          >
            {inviting ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </Paper>
      </Box>
    );
  };
  
  return (
    <Box>
      {/* Success Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 72, height: 72, borderRadius: '50%',
          bgcolor: `${statusColors.success}14`, mb: 2,
        }}>
          <CheckCircle sx={{ fontSize: 40, color: statusColors.success }} />
        </Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: text.primary }}>
          Project Created!
        </Typography>
        <Typography variant="body1" sx={{ color: text.secondary }}>
          Now let's find the right contractor for your project.
        </Typography>
      </Box>

      {/* What happens next - trust messaging */}
      <Box sx={{
        mb: 3, p: 2.5, borderRadius: 2,
        bgcolor: `${brand.primary}08`,
        border: `1px solid ${brand.primary}20`,
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand.primaryDark, mb: 1 }}>
          What happens next?
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {[
            'Your project is visible to verified contractors in your area',
            'You can also invite or search for contractors yourself',
            'You only pay when you approve completed work — your money is protected',
          ].map((item, i) => (
            <Typography key={i} component="li" variant="body2" sx={{ color: text.secondary, mb: 0.5 }}>
              {item}
            </Typography>
          ))}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Contractor Selection Tabs */}
      <Paper sx={{ mb: 3, border: `1px solid ${border.light}`, boxShadow: shadow.sm }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' },
            '& .Mui-selected': { color: brand.primary },
            '& .MuiTabs-indicator': { bgcolor: brand.primary },
          }}
        >
          <Tab
            icon={<AutoAwesome />}
            label="AI Matched"
            iconPosition="start"
          />
          <Tab
            icon={<History />}
            label="Previous"
            iconPosition="start"
          />
          <Tab
            icon={<Public />}
            label="Marketplace"
            iconPosition="start"
          />
          <Tab
            icon={<PersonAdd />}
            label="Invite"
            iconPosition="start"
          />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderAIMatchedTab()}
          {activeTab === 1 && renderPreviousContractorsTab()}
          {activeTab === 2 && renderMarketplaceTab()}
          {activeTab === 3 && renderInviteTab()}
        </Box>
      </Paper>
      
      {/* Action Buttons */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<Search />}
            onClick={() => {
              navigate(`/contractors`);
              onClose();
            }}
            sx={{
              borderColor: brand.primary,
              color: brand.primary,
              fontWeight: 600,
              '&:hover': { bgcolor: `${brand.primary}08`, borderColor: brand.primaryDark },
            }}
          >
            Browse All Contractors
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => {
              navigate(`/projects/${projectId}`);
              onClose();
            }}
            sx={{
              background: brand.gradient,
              fontWeight: 600,
              '&:hover': { background: brand.gradientHover },
            }}
          >
            Go to Project Dashboard
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            onClick={onClose}
            sx={{ color: text.tertiary }}
          >
            I'll Do This Later
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PostCreationFlow;
